import { useRef, useEffect, useCallback } from "react";
import { useFlushOnPageHide } from "@/hooks/use-flush-on-page-hide";
import { useMutation } from "@apollo/client";
import { UPDATE_CLIP_DATA } from "@/queries/queries";
import type { SceneTransition } from "@/lib/transitions/types";

interface Options {
  clipId: string | null;
  transitions: SceneTransition[] | null;
  /** The clip the CURRENT `transitions` belong to — same stale-window tag as
   *  useAutoSaveClipData; see the rationale there. */
  loadedClipId: string | null;
  enabled: boolean;
  delay?: number;
  onSaved?: (clipId: string, data: { transitions: SceneTransition[] }) => void;
}

type PendingWrite = {
  clipId: string;
  transitions: SceneTransition[];
  serialized: string;
};

/** Debounced persistence of per-cut scene transitions (clip.data.transitions).
 *  Clone of useAutoSaveCropKeyframes: pending writes are flushed (never
 *  dropped) on clip switch and unmount; the first run whose data carries the
 *  clip's own tag is the load echo and seeds the diff baseline. */
export function useAutoSaveTransitions({
  clipId,
  transitions,
  loadedClipId,
  enabled,
  delay = 500,
  onSaved,
}: Options) {
  const [updateClipData, { loading: saving }] = useMutation(UPDATE_CLIP_DATA);
  const timeoutRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const pendingRef = useRef<PendingWrite | null>(null);
  const lastSavedRef = useRef<string>("");
  const awaitingLoadRef = useRef<boolean>(true);
  const currentClipIdRef = useRef<string | null>(null);
  const onSavedRef = useRef(onSaved);

  useEffect(() => {
    onSavedRef.current = onSaved;
  }, [onSaved]);

  const performSave = useCallback(
    async (write: PendingWrite) => {
      try {
        const payload = { transitions: write.transitions };
        await updateClipData({
          variables: { id: write.clipId, data: payload },
        });
        lastSavedRef.current = write.serialized;
        onSavedRef.current?.(write.clipId, payload);
      } catch (error) {
        console.error("Auto-save transitions failed:", error);
      }
    },
    [updateClipData],
  );

  /** Fire the pending write NOW (fire-and-forget). Never drops it. */
  const firePending = useCallback(() => {
    if (timeoutRef.current) {
      clearTimeout(timeoutRef.current);
      timeoutRef.current = null;
    }
    const pending = pendingRef.current;
    pendingRef.current = null;
    if (pending) void performSave(pending);
  }, [performSave]);
  // Tab close never unmounts: fire the pending write as a keepalive request.
  useFlushOnPageHide(firePending);
  const firePendingRef = useRef(firePending);
  useEffect(() => {
    firePendingRef.current = firePending;
  }, [firePending]);

  // Clip switch: flush the old clip's pending write, await the new load.
  useEffect(() => {
    if (clipId !== currentClipIdRef.current) {
      firePending();
      currentClipIdRef.current = clipId;
      awaitingLoadRef.current = true;
      lastSavedRef.current = "";
    }
  }, [clipId, firePending]);

  useEffect(() => {
    if (!enabled || !clipId) return;
    // Stale window: these transitions belong to another clip.
    if (loadedClipId !== clipId) return;

    const serialized = transitions ? JSON.stringify(transitions) : "";
    if (awaitingLoadRef.current) {
      awaitingLoadRef.current = false;
      lastSavedRef.current = serialized;
      return;
    }
    if (transitions === null) return;
    if (serialized === lastSavedRef.current) return;

    if (timeoutRef.current) clearTimeout(timeoutRef.current);
    pendingRef.current = { clipId, transitions, serialized };
    timeoutRef.current = setTimeout(() => {
      timeoutRef.current = null;
      const pending = pendingRef.current;
      pendingRef.current = null;
      if (pending) void performSave(pending);
    }, delay);
    // Clears only the timer; pendingRef survives for the unmount flush.
    return () => {
      if (timeoutRef.current) clearTimeout(timeoutRef.current);
    };
  }, [transitions, loadedClipId, clipId, enabled, delay, performSave]);

  // Unmount: flush instead of dropping.
  useEffect(() => () => firePendingRef.current(), []);

  return { saving };
}
